'use client';

import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import {
  AdornedInput,
  Alert,
  Button,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Paragraph,
} from '@zillow/constellation-10';
import { css } from '@/styled-system/css';
import { getPathname } from '@/src/i18n/navigation';
import type { SupportedLocale } from '@/src/i18n/routing';
import type { AuthResponse } from '@/src/lib/api/endpoints/user';
import { isValidEmail } from '@/src/lib/auth/email';
import { navigateToExternalAuth } from '@/src/lib/auth/provider-navigation';
import { isSafeRedirect } from '@/src/lib/auth/safe-redirect';
import type { AuthMessageKey } from '@/src/lib/auth/status';
import { mapAuthStatusToMessageKey } from '@/src/lib/auth/status';
import { useEmailPasswordLogin, useFreepassLogin } from '@/src/lib/hooks/query/useUserQuery';
import { AppleSignInButton } from '@/src/components/features/auth/AppleSignInButton';
import { GoogleSignInButton } from '@/src/components/features/auth/GoogleSignInButton';
import { form } from '@/src/components/features/auth/login/login.styles';

type LoginStep = 'email' | 'password' | 'email-sent' | 'deactivated';

type LoginFormProps = {
  callbackErrorKey?: AuthMessageKey | null;
  confirmationHeadingLevel?: 2 | 3;
  emailFooter?: ReactNode;
  // In-page sign-ins only (email/password + freepass); caller redirects. OAuth + magic link don't fire this.
  onAuthenticated: (response: AuthResponse) => void;
  onFreepassError?: (error: unknown) => void;
  onPasswordError?: (error: unknown) => void;
  passwordFooter?: ReactNode;
  // Landing page for the navigate-away sign-ins (OAuth + magic link); they don't fire onAuthenticated.
  postLoginRedirect?: string;
};

const centeredStack = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'default',
  textAlign: 'center',
});

const oauthStack = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'default',
});

const separatorText = css({
  color: 'text.subtle',
  marginBlock: 'loose',
  textAlign: 'center',
  textTransform: 'uppercase',
});

function buildVerifyUrl(locale: string, postLoginRedirect?: string): string {
  const verifyPath = getPathname({ href: '/login/verify', locale: locale as SupportedLocale });

  return postLoginRedirect ? `${verifyPath}?redirect=${encodeURIComponent(postLoginRedirect)}` : verifyPath;
}

export function LoginForm({
  callbackErrorKey,
  confirmationHeadingLevel = 2,
  emailFooter,
  onAuthenticated,
  onFreepassError,
  onPasswordError,
  passwordFooter,
  postLoginRedirect,
}: LoginFormProps) {
  const locale = useLocale();
  const t = useTranslations('auth');
  const freepassMutation = useFreepassLogin();
  const passwordMutation = useEmailPasswordLogin();
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const verifyUrl = buildVerifyUrl(locale, postLoginRedirect);

  const submitFreepass = (targetEmail: string) => {
    freepassMutation.mutate(
      { email: targetEmail, url: verifyUrl },
      {
        onSuccess: (response) => {
          if (response.data?.forwardUrl) {
            const url = response.data.forwardUrl;
            if (url.startsWith('https://') || url.startsWith('http://') || isSafeRedirect(url)) {
              navigateToExternalAuth(url, window.location.pathname);
              return;
            }
          }
          if (response.loggedIn) {
            onAuthenticated(response);
            return;
          }
          if (response.status === 'USER_ALREADY_EXISTS') {
            setStep('password');
            return;
          }
          if (response.status === 'ACCOUNT_DEACTIVATED') {
            setStep('deactivated');
            return;
          }
          // EMAIL_SENT is the backend's success status for a magic-link send (an existing
          // passwordless account); an empty status is the legacy no-status success. Both mean
          // the link is on its way — show the check-your-email confirmation, not an error.
          if (!response.status || response.status === 'EMAIL_SENT') {
            setStep('email-sent');
            return;
          }
          setError(t(mapAuthStatusToMessageKey(response.status)));
        },
        onError: (mutationError) => {
          onFreepassError?.(mutationError);
          setError(t('errorGeneric'));
        },
      },
    );
  };

  const handleEmailSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError('');

    const trimmed = email.trim();
    if (!trimmed) {
      setError(t('errorEnterEmail'));
      return;
    }
    if (!isValidEmail(trimmed)) {
      setError(t('errorInvalidEmail'));
      return;
    }

    setEmail(trimmed);
    submitFreepass(trimmed);
  };

  const handlePasswordSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError('');

    if (!password.trim()) {
      setError(t('errorEnterPassword'));
      return;
    }

    passwordMutation.mutate(
      { email, password },
      {
        onSuccess: (response) => {
          if (response.loggedIn) {
            onAuthenticated(response);
            return;
          }
          setError(t(mapAuthStatusToMessageKey(response.status)));
        },
        onError: (mutationError) => {
          onPasswordError?.(mutationError);
          setError(t('errorGeneric'));
        },
      },
    );
  };

  const handleChangeEmail = () => {
    setStep('email');
    setPassword('');
    setError('');
  };

  const renderEmailStep = () => (
    <>
      {callbackErrorKey && (
        <Alert tone="critical" role="alert" css={{ marginBottom: 'default' }} data-testid="login-callback-error">
          {t(callbackErrorKey)}
        </Alert>
      )}
      <form className={form} onSubmit={handleEmailSubmit} noValidate>
        <FormField
          label={<Label>{t('email')}</Label>}
          error={!!error}
          description={error ? <FormHelp>{error}</FormHelp> : undefined}
          control={
            <Input
              type="email"
              placeholder={t('emailPlaceholder')}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
            />
          }
        />
        <Button type="submit" tone="brand" emphasis="filled" size="lg" fluid disabled={freepassMutation.isPending}>
          {freepassMutation.isPending ? t('signingIn') : t('continue')}
        </Button>
      </form>

      <Paragraph textStyle="body-sm" className={separatorText}>
        {t('or')}
      </Paragraph>

      <div className={oauthStack}>
        <GoogleSignInButton postLoginRedirect={postLoginRedirect} />
        <AppleSignInButton postLoginRedirect={postLoginRedirect} />
      </div>

      {emailFooter}
    </>
  );

  const renderPasswordStep = () => (
    <>
      <form className={form} onSubmit={handlePasswordSubmit} noValidate>
        <FormField
          label={<Label>{t('email')}</Label>}
          control={
            <AdornedInput
              readOnly
              input={<AdornedInput.Input value={email} tabIndex={-1} aria-label={t('email')} />}
              endAdornment={
                <AdornedInput.Adornment asChild aria-hidden={false}>
                  <Button type="button" emphasis="bare" size="sm" tone="brand" onClick={handleChangeEmail}>
                    {t('change')}
                  </Button>
                </AdornedInput.Adornment>
              }
            />
          }
        />

        <FormField
          label={<Label>{t('password')}</Label>}
          error={!!error}
          description={error ? <FormHelp>{error}</FormHelp> : undefined}
          control={
            <Input
              type="password"
              placeholder={t('passwordPlaceholder')}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              autoFocus
            />
          }
        />

        <Button type="submit" tone="brand" emphasis="filled" size="lg" fluid disabled={passwordMutation.isPending}>
          {passwordMutation.isPending ? t('signingIn') : t('continue')}
        </Button>
      </form>

      {passwordFooter}
    </>
  );

  const renderEmailSentStep = () => (
    <div className={centeredStack}>
      <Heading level={confirmationHeadingLevel} textStyle="heading-xs">
        {t('checkYourEmailHeading')}
      </Heading>
      <Paragraph>{t('checkYourEmailBody', { email })}</Paragraph>
      {error && (
        <Alert tone="critical" role="alert">
          {error}
        </Alert>
      )}
      <Button
        type="button"
        tone="brand"
        emphasis="outlined"
        size="lg"
        fluid
        onClick={() => submitFreepass(email)}
        disabled={freepassMutation.isPending}
      >
        {freepassMutation.isPending ? t('resendingEmail') : t('resendEmail')}
      </Button>
      <Button type="button" emphasis="bare" size="sm" tone="brand" fluid onClick={handleChangeEmail}>
        {t('useDifferentEmail')}
      </Button>
    </div>
  );

  const renderDeactivatedStep = () => (
    <div className={centeredStack}>
      <Heading level={confirmationHeadingLevel} textStyle="heading-xs">
        {t('accountDeactivatedHeading')}
      </Heading>
      <Paragraph>{t('accountDeactivatedBody')}</Paragraph>
      <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
        {t('accountDeactivatedContact')}
      </Paragraph>
      <Button type="button" emphasis="bare" size="sm" tone="brand" fluid onClick={handleChangeEmail}>
        {t('useDifferentEmail')}
      </Button>
    </div>
  );

  const bodyByStep: Record<LoginStep, () => ReactNode> = {
    email: renderEmailStep,
    password: renderPasswordStep,
    'email-sent': renderEmailSentStep,
    deactivated: renderDeactivatedStep,
  };

  return bodyByStep[step]();
}
